import { useState, useEffect, useMemo } from 'react'
import { enqueueSnackbar } from 'notistack'
import { DateTime } from 'luxon'
import { formatDateForDatabase } from '@utils'
import { logout } from '@utils/sessions'
import { RoleName } from '@components/SelectRole'
import {
    OrganizationServices,
    OrgDefaultLimit,
    ServicePrices,
    UserLimit,
    UsersServicesAllowed,
    UsersServicesBlocked,
    GroupServiceAllowed,
    GroupServiceBlocked,
    GroupLimit,
    NotesLimitLevel,
    NotesOrderReason,
} from '@types'
import { SessionsData } from '../components/SessionsDataGrid'
import API from '@enums/api'
import { GridGetRowsResponse } from '@mui/x-data-grid-pro'
import useDataSource from '@hooks/useDataSource'

export type UserData = {
    accountProfile: {
        id: string
        email: string
        name: string
        first_name: string
        last_name: string
        type: string
        created_at: string
        updated_at: string
        deleted_at: string | null
        first_logged_in: string | null
        last_logged_in: string | null
    }
    adminProfile?: {
        id: string
        beehive_id: string
        sms_id: string | null
        nte_id: string | null
        org_id: string | null
        email: string
        first_name: string | null
        last_name: string | null
        active: { type: string; data: number[] }
        limit_users: number | null
        role: string | null
        reference_id: string
        created_date: string | null
        comment: string | null
        phone: string
        api_token: string
        last_login: string | null
        timezone: string | null
        token_valid: string | null
        deleted_date: string | null
        group?: {
            id: string
            name: string
            org_id: string
            description: string
            created_at: string
            updated_at: string
            deleted_at: string | null
        }
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        services: any[]
        subscriptions: Record<
            string,
            {
                id: number
                user_id: string | null
                org_id: string
                project: string
                sub_type: string
                created_date: string
                to_date: string | null
            }
        >
        orgAdmins:
            | {
                  first_name: string | null
                  last_name: string | null
                  id: string | null
                  email: string | null
                  role: string | null
              }[]
            | null
        licence?: {
            tier: { name: string }
        }
    }
    mpProfile: {
        mpOrderCount?: { count: number | string }
        mpOrders: {
            id: string
            orderType: string
            orderStatus:
                | 'completed'
                | 'task-cancelled--no-discussion'
                | 'task-cancelled--missing-media'
                | 'task-in-progress'
                | 'task-cancelled--media-quality'
                | 'order-creation-failed'
                | 'pending-task-completion'
                | 'task-cancelled--other'
                | 'task-creation-failed'
                | 'task-cancelled--duplicate'
                | 'pending-payment'
            totalAmount: number | null
            unitAmount: number | null
            currency: string | null
            paidBy: 'user' | 'organization'
            createdAt: string
            updatedAt: string
            sessionId: string
        }[]
        adminOrders?: {
            id: string
            created_date: string
            title: string
            service_type: string
            status:
                | 'completed'
                | 'cancelled - duplicated task'
                | 'task-cancelled--media-quality'
                | 'cancelled - no academic discussion'
                | 'task-cancelled--duplicate'
                | 'task-cancelled--missing-media'
                | 'cancelled - audio/video file not available'
                | 'pending'
                | 'cancelled - poor audio/inaudible'
                | 'task-cancelled--no-discussion'
                | 'cancelled - delete on request'
                | 'cancelled - audio issue'
                | 'cancelled - test task'
                | 'emailed'
                | 'cancelled user cancellation'
                | 'task-cancelled--other'
                | 'in progress'
                | null
            duration: number
            rate: number
            turnaround_time: number
            cost: number
            cost_currency: string
            last_updated: string
            date_finished: string
            source_id: string
        }[]
        sessions: SessionsData[]
    }
    settings?: {
        /** Why they can or cannot order, which the rule pane needs. */
        reason?: NotesOrderReason
        /** Which level decided whether they may order. */
        settingLevel?: 'user' | 'group' | 'org'
        /** Which level supplied the cap, which is often not settingLevel. */
        limitLevel?: NotesLimitLevel | null
        limitSeconds?: number | null
        orgSetting?: {
            setting_type: 'org'
            setting: Pick<
                OrganizationServices,
                'service_id' | 'from_date' | 'to_date'
            >
            service: ServicePrices
        }
        userSetting?: {
            setting_type: 'allowed' | 'blocked'
            setting: Pick<
                UsersServicesAllowed | UsersServicesBlocked,
                'service_id' | 'from_date' | 'to_date'
            >
            service: ServicePrices
        }
        groupSetting?: {
            setting_type: 'allowed' | 'blocked'
            setting: Pick<
                GroupServiceAllowed | GroupServiceBlocked,
                'service_id' | 'from_date' | 'to_date'
            >
            service: ServicePrices
        }

        orgDefLimit?: {
            type: 'org'
            limit: OrgDefaultLimit
            service: ServicePrices
        }
        userLimit?: {
            type: 'user'
            limit: UserLimit
            service: ServicePrices
        }
        groupLimit?: {
            type: 'group'
            limit: GroupLimit
            service: ServicePrices
        }
        usage: number
    }
    licenceTier: LicenceTier
}

export type LicenceTier = 'STANDARD' | 'DEMO' | 'PRO' | 'BASIC' | 'FREE' | ''

/**
 * Whether the RULE permits ordering, which is not the same question as
 * whether the student can order right now.
 *
 * `canOrder` is the whole verdict and folds in the allowance, so deriving the
 * rule's status from it printed "Inactive" over a perfectly good rule
 * whenever a student was over their cap. Someone then went through the
 * database looking for a block that did not exist. The rule and the cap are
 * resolved independently and the reason says which of them refused.
 *
 * 'limit-reached' is only produced when the rule permitted: habitat-admin
 * sets it as `permission === 'allowed' && !isUnderLimit`, so it can never
 * appear over a block.
 *
 * `canOrder` is consulted first, so a reason this build does not know about
 * cannot silently read as blocked.
 */
function rulePermits(
    reason: NotesOrderReason | undefined,
    canOrder: boolean
): boolean {
    if (canOrder) return true
    return reason === 'limit-reached'
}

export default function useUserPage() {
    const [searchError, setSearchError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [user, setUser] = useState<UserData | null>(null)
    const [deleteLock, setDeleteLock] = useState(true)
    const [openEdit, setOpenEdit] = useState(false)
    const [newOrg, setNewOrg] = useState<string | undefined>(undefined)
    const [newRole, setNewRole] = useState<RoleName | undefined>(undefined)
    const [settingDate, setSettingDate] = useState<DateTime | null | undefined>(
        DateTime.now()
    )
    const [loadingSettings, setLoadingSettings] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [deleting, setDeleting] = useState(false)

    const licenceTier = useMemo<LicenceTier>(() => {
        // Admins get STANDARD access on MP regardless of an explicit licence.
        if (
            user?.adminProfile?.role === 'admin' ||
            user?.adminProfile?.role === 'atsp'
        ) {
            return 'STANDARD'
        }

        const tierName = user?.adminProfile?.licence?.tier?.name
        if (!tierName) return 'DEMO'

        return tierName.toUpperCase() as LicenceTier
    }, [user])

    useEffect(() => {
        fetchUserByQueryParams()
    }, [])

    // Load from ?q=...
    const fetchUserByQueryParams = async () => {
        const q = new URLSearchParams(window.location.search).get('q')
        if (!q) return
        setLoading(true)
        const userRes = await API.overviews.getUserOverview({ id: q })

        if (!userRes.ok) {
            enqueueSnackbar(
                `Get User Error: ${userRes.status} ${userRes.message}`,
                { variant: 'warning' }
            )
            setLoading(false)
            return
        }
        const fetchedUser = userRes.value

        let settings: UserData['settings'] | undefined = undefined
        if (fetchedUser.adminProfile?.id) {
            settings = await getUserSettingsById(fetchedUser.adminProfile.id)
        } else {
            fetchedUser.adminProfile = undefined
        }

        const userToSet: UserData = {
            ...fetchedUser,
            mpProfile: {
                ...fetchedUser.mpProfile,
                sessions: [],
            },
        }
        if (settings) {
            userToSet.settings = settings
        }

        const userEmail = fetchedUser.accountProfile.email

        setSearchValue(userEmail)

        setUser(userToSet)
        setDeleteLock(true)
        setLoading(false)
    }

    // Fetch user by email or id
    const fetchFullUserByEmail = async (
        email: string
    ): Promise<UserData | null> => {
        const res = await API.overviews.getUserOverview({ email })
        if (!res.ok) {
            if (res.status === 404) {
                setSearchError(
                    res.message === 'user_not_found'
                        ? 'User could not be found.'
                        : `Error: ${res.status} ${res.message}`
                )
                return null
            }
            setSearchError(`Error: ${res.status} ${res.message}`)
            return null
        }
        setSearchError(null)
        return res.value
    }

    const onSearchSubmit = async ({ email }: { email: string }) => {
        setSearchValue(email)
        const info = await fetchFullUserByEmail(email)
        if (!info) {
            setUser(null)
            return
        }

        if (info.adminProfile?.id) {
            info.settings = await getUserSettingsById(info.adminProfile.id)
        }

        setUser(info)
        window.history.pushState(null, '', `?q=${info.accountProfile.id}`)
    }

    const onUserSearch = async (query: string) => {
        const res = await API.searches.users(query)
        if (!res.ok) throw new Error('Failed to search users')
        return res.value.map((u: { email: string; id: string }) => ({
            email: u.email,
            id: u.id,
        }))
    }

    const onEditUserSubmit: React.FormEventHandler<HTMLFormElement> = async (
        e
    ) => {
        e.preventDefault()
        if (!user) return
        const body: { id: string; org_id?: string; role?: string } = {
            id: user.accountProfile.id,
            org_id: newOrg,
        }
        if (newRole !== user.adminProfile?.role) body.role = newRole
        const res = await API.actions.updateUser(body)
        if (!res.ok) {
            if (res.status === 401) {
                logout()
                return
            }
            enqueueSnackbar('Failed to update user', { variant: 'error' })
            setOpenEdit(false)
            return
        }
        await fetchUserByQueryParams()

        const { errors } = res.value

        /**
         * `org` and `role` are a plain did-it-work; `email` reports per
         * system, so it is flattened into one warning per system that
         * failed. A null means that part was fine.
         */
        const failed = [
            errors.org ? 'org' : null,
            errors.role ? 'role' : null,
            ...Object.entries(errors.email).map(([system, error]) =>
                error ? `email (${system})` : null
            ),
        ].filter((part): part is string => part !== null)

        if (failed.length === 0) {
            enqueueSnackbar('User updated successfully', {
                variant: 'success',
                onClose: () => setOpenEdit(false),
            })
            return
        }

        failed.forEach((part) =>
            enqueueSnackbar(`Could not update ${part}`, { variant: 'warning' })
        )
    }

    const onDelete = async () => {
        if (!user) return
        setDeleting(true)
        const res = await API.actions.deleteUser(user.accountProfile.id)
        if (!res.ok) {
            enqueueSnackbar(`${res.status}: Failed to delete user`, {
                variant: 'error',
            })
            setDeleting(false)
            return
        }
        await fetchUserByQueryParams()
        enqueueSnackbar('User deleted successfully', { variant: 'success' })
        const { errors } = res.value
        Object.entries(errors).forEach(([k, v]) => {
            if (v) enqueueSnackbar(`${k}: ${v}`, { variant: 'warning' })
        })
        setDeleting(false)
    }

    const getUserSettingsById = async (
        userId: string
    ): Promise<UserData['settings']> => {
        const date = formatDateForDatabase(settingDate, 'end')

        const res = await API.ha.settings.getUserSettings(userId, date)

        if (!res.ok) {
            enqueueSnackbar(`${res.status} ${res.message}`, {
                variant: 'error',
            })
            setLoadingSettings(false)
            return {
                usage: 0,
            }
        }

        const {
            canOrder,
            reason,
            service,
            setting,
            settingLevel,
            limit,
            limitLevel,
            limitSeconds,
            usage,
        } = res.value

        const data: UserData['settings'] = {
            usage,
            reason,
            settingLevel,
            limitLevel,
            limitSeconds,
        }

        if (setting && service) {
            switch (settingLevel) {
                case 'user':
                    data.userSetting = {
                        setting_type: rulePermits(reason, canOrder)
                            ? 'allowed'
                            : 'blocked',
                        service,
                        setting: {
                            service_id: setting.service_id,
                            from_date: setting.from_date,
                            to_date: setting.to_date,
                        },
                    }
                    if (!limit) break
                    data.userLimit = {
                        type: 'user',
                        service,
                        limit,
                    }
                    break
                case 'group':
                    data.groupSetting = {
                        setting_type: rulePermits(reason, canOrder)
                            ? 'allowed'
                            : 'blocked',
                        service,
                        setting: {
                            service_id: setting.service_id,
                            from_date: setting.from_date,
                            to_date: setting.to_date,
                        },
                    }
                    if (!limit) break
                    data.groupLimit = {
                        type: 'group',
                        service,
                        limit,
                    }
                    break
                case 'org':
                    data.orgSetting = {
                        setting_type: 'org',
                        service,
                        setting: {
                            service_id: setting.service_id,
                            from_date: setting.from_date,
                            to_date: setting.to_date,
                        },
                    }
                    if (!limit) break
                    data.orgDefLimit = {
                        type: 'org',
                        service,
                        limit,
                    }
                    break
            }
        }
        return data
    }

    const getUserSettings = async () => {
        if (!user) {
            enqueueSnackbar(`Can't do this without a user!`, {
                variant: 'warning',
            })
            return
        }

        if (!user.adminProfile?.id) return
        setLoadingSettings(true)

        const data = await getUserSettingsById(user.adminProfile.id)

        setUser((u) => (u ? { ...u, settings: data } : u))
        setLoadingSettings(false)

        enqueueSnackbar('Successfully retrieved settings and usage', {
            variant: 'success',
        })
    }

    const sessionsData = useDataSource({
        getRows: async ({
            paginationModel,
            sortModel,
            filterModel,
        }): Promise<GridGetRowsResponse> => {
            const q = new URLSearchParams(window.location.search).get('q')
            if (!q) {
                throw new Error('No userID')
            }

            if (!paginationModel || !sortModel || !filterModel) {
                throw new Error('No DataGrid models for sessions')
            }

            const res = await API.overviews.sessions({
                userId: q,
                paginationModel,
                sortModel,
                filterModel,
            })

            if (!res.ok) {
                const message = `Failed to get session data: ${res.status} ${res.message}`
                enqueueSnackbar({
                    variant: 'error',
                    message,
                })
                throw new Error(message)
            }

            const { rows, total_rows } = res.value

            return {
                rows,
                rowCount: total_rows,
            }
        },
    })

    const ordersData = useDataSource({
        getRows: async ({
            paginationModel,
            sortModel,
            filterModel,
        }): Promise<GridGetRowsResponse> => {
            const q = new URLSearchParams(window.location.search).get('q')
            if (!q) {
                throw new Error('No userID')
            }

            if (!paginationModel || !sortModel || !filterModel) {
                throw new Error('No DataGrid models for orders')
            }

            const res = await API.overviews.orders({
                userId: q,
                paginationModel,
                sortModel,
                filterModel,
            })

            if (!res.ok) {
                const message = `Failed to get orders data: ${res.status} ${res.message}`
                enqueueSnackbar({
                    variant: 'error',
                    message,
                })
                throw new Error(message)
            }

            return res.value
        },
    })

    if (user) {
        user.licenceTier = licenceTier
    }

    return {
        searchError,
        loading,
        user,
        deleteLock,
        openEdit,
        newOrg,
        newRole,
        settingDate,
        loadingSettings,
        searchValue,
        deleting,
        sessionsData,
        ordersData,
        setSearchValue,
        setOpenEdit,
        setNewOrg,
        setNewRole,
        setSettingDate,
        fetchFullUserByEmail,
        fetchUserByQueryParams,
        onSearchSubmit,
        onUserSearch,
        onEditUserSubmit,
        onDelete,
        getUserSettings,
        setDeleteLock,
    }
}
